/* eslint-disable filename-rules/match -- versioned style-config files are named after the DLS version */
import { tagAnatomy as parts } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers } from '@chakra-ui/react';
import type { StyleFunctionProps } from '@chakra-ui/theme-tools';

const { definePartsStyle, defineMultiStyleConfig } =
  createMultiStyleConfigHelpers(parts.keys);

type TagColorScheme =
  | 'neutral'
  | 'brand'
  | 'positive'
  | 'warning'
  | 'negative'
  | 'gold';

const colorSchemeOf = (props: StyleFunctionProps): TagColorScheme =>
  (props.colorScheme as TagColorScheme) ?? 'neutral';

/**
 * Subtle-fill hover token. The neutral family exposes `fill-active`; the
 * sentiment families expose `fill-hover`.
 */
const subtleHoverBg = (colorScheme: TagColorScheme): string =>
  colorScheme === 'neutral'
    ? 'neutral.fill-active'
    : `${colorScheme}.fill-hover`;

/** Solid sentiment fill with inverted text. */
const primary = definePartsStyle((props) => {
  const colorScheme = colorSchemeOf(props);
  return {
    // `color` lives on the container so both the label (via inheritance) and the
    // leading icon (via `fill="currentColor"`) pick up the sentiment color.
    container: {
      bg: `${colorScheme}.solid-primary`,
      color:
        colorScheme === 'neutral'
          ? 'neutral-invert.fg-primary'
          : 'misc.white-fix-primary',
      _hover: { bg: `${colorScheme}.solid-hover` },
    },
  };
});

/** Subtle sentiment fill with sentiment text. */
const secondary = definePartsStyle((props) => {
  const colorScheme = colorSchemeOf(props);
  return {
    container: {
      bg: `${colorScheme}.fill-subtle`,
      color: `${colorScheme}.fg-primary`,
      _hover: { bg: subtleHoverBg(colorScheme) },
    },
  };
});

/** Subtle sentiment fill with de-emphasized text. */
const secondarySubtle = definePartsStyle((props) => {
  const colorScheme = colorSchemeOf(props);
  return {
    container: {
      bg: `${colorScheme}.fill-subtle`,
      color: `${colorScheme}.fg-secondary`,
      _hover: { bg: subtleHoverBg(colorScheme) },
    },
  };
});

/** TODO - fix lineHeight here, based on this ticket - https://linear.app/republic/issue/CLG-190/review-how-lineheight-is-used-in-tag-component-and-textstyles */
export const styleConfig = defineMultiStyleConfig({
  baseStyle: definePartsStyle({
    container: {
      display: 'inline-flex',
      alignItems: 'center',
      width: 'fit-content',
      fontWeight: 'medium',
      whiteSpace: 'nowrap',
    },
    label: {
      fontWeight: 'medium',
    },
  }),
  sizes: {
    sm: definePartsStyle({
      container: {
        px: '2',
        py: '1',
        gap: '1',
        minH: '7',
        borderRadius: 'md',
      },
      label: {
        fontSize: 'sm-md',
        lineHeight: '1.25rem',
        letterSpacing: 'less-tight',
      },
    }),
    xs: definePartsStyle({
      container: {
        px: '1.5',
        py: '0.5',
        gap: '1',
        minH: '5',
        borderRadius: 'sm-md',
      },
      label: {
        fontSize: 'sm',
        lineHeight: '1rem',
        letterSpacing: 'normal',
      },
    }),
    '2xs': definePartsStyle({
      container: {
        px: '1',
        py: '0.5',
        gap: '1',
        minH: '4',
        borderRadius: 'sm-md',
      },
      label: {
        fontSize: 'xs',
        lineHeight: '0.75rem',
        letterSpacing: 'normal',
      },
    }),
  },
  variants: {
    primary,
    secondary,
    'secondary-subtle': secondarySubtle,
  },
  defaultProps: {
    variant: 'secondary',
    colorScheme: 'neutral',
    size: 'sm',
  },
});
